/**
 * 神策身份在浏览器本地缓存中的 distinct_id key。
 */
const DISTINCT_ID_STORAGE_KEY = 'zl-distinct_id';

/**
 * 神策身份在浏览器本地缓存中的 anonymous_id key。
 */
const ANONYMOUS_ID_STORAGE_KEY = 'zl-anonymous_id';

/**
 * 后端接口透传使用的 distinct-id cookie key。
 */
const DISTINCT_ID_COOKIE_KEY = 'distinct-id';

/**
 * 后端接口透传使用的 anonymous-id cookie key。
 */
const ANONYMOUS_ID_COOKIE_KEY = 'anonymous-id';

/**
 * 神策身份 cookie 的有效期，保持与 axios 请求头写入逻辑一致。
 */
const IDENTITY_COOKIE_MAX_AGE_SECONDS = 31536000;

export interface SensorsIdentity {
  distinctId?: string;
  anonymousId?: string;
}

/**
 * 过滤神策身份中的无效空值。
 */
function normalizeIdentityValue(value: unknown): string | undefined {
  if (
    value === '' ||
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return undefined;
  }

  return String(value);
}

/**
 * 安全读取 Storage，兼容隐私模式或跨端容器禁用 storage 的场景。
 */
function readStorageValue(storage: Storage | undefined, key: string): string | undefined {
  try {
    return normalizeIdentityValue(storage?.getItem(key));
  } catch {
    return undefined;
  }
}

/**
 * 安全获取 sessionStorage，兼容访问 storage getter 即抛错的浏览器模式。
 */
function getSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * 安全获取 localStorage，兼容访问 storage getter 即抛错的浏览器模式。
 */
function getLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * 安全写入 Storage，避免隐私模式配额异常影响主链路。
 */
function writeStorageValue(
  storage: Storage | undefined,
  key: string,
  value: string | undefined,
): void {
  if (!value) return;

  try {
    storage?.setItem(key, value);
  } catch {
    // 身份缓存失败不应阻断神策或 Snowplow 上报。
  }
}

/**
 * 从 document.cookie 中读取指定身份字段。
 */
function readCookieValue(key: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const matchedCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${key}=`));
  if (!matchedCookie) return undefined;

  return normalizeIdentityValue(decodeURIComponent(matchedCookie.slice(key.length + 1)));
}

/**
 * 写入身份 cookie，供服务端请求头和后续首屏 fallback 复用。
 */
function writeCookieValue(key: string, value: string | undefined): void {
  if (!value || typeof document === 'undefined') return;

  document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${IDENTITY_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

/**
 * 从一个神策 SDK 实例中读取 distinct_id 与 anonymous_id。
 */
function readSensorsIdentityFromInstance(sensors: unknown): SensorsIdentity {
  const sensorsInstance = sensors as
    | {
        store?: { getDistinctId?: () => unknown };
        quick?: (command: string) => unknown;
      }
    | undefined;

  if (!sensorsInstance) return {};

  try {
    return {
      distinctId: normalizeIdentityValue(sensorsInstance.store?.getDistinctId?.()),
      anonymousId: normalizeIdentityValue(sensorsInstance.quick?.('getAnonymousID')),
    };
  } catch {
    return {};
  }
}

/**
 * 收集当前窗口和父窗口中可访问的神策 SDK 实例。
 */
function getSensorsInstances(): unknown[] {
  if (typeof window === 'undefined') return [];

  const instances: unknown[] = [window.sensorsDataAnalytic201505, window.sensors];

  try {
    if (window.parent && window.parent !== window) {
      instances.push(window.parent.sensorsDataAnalytic201505, window.parent.sensors);
    }
  } catch {
    // 跨域 iframe 访问 parent 可能抛错，此处只跳过 parent fallback。
  }

  return instances.filter(Boolean);
}

/**
 * 读取当前可用的神策身份，优先使用 SDK 实时值，再回退到 storage 与 cookie。
 */
export function getSensorsIdentity(): SensorsIdentity {
  const identity: SensorsIdentity = {};

  for (const sensors of getSensorsInstances()) {
    const sensorsIdentity = readSensorsIdentityFromInstance(sensors);
    identity.distinctId = identity.distinctId ?? sensorsIdentity.distinctId;
    identity.anonymousId = identity.anonymousId ?? sensorsIdentity.anonymousId;

    if (identity.distinctId && identity.anonymousId) {
      return identity;
    }
  }

  identity.distinctId =
    identity.distinctId ??
    readStorageValue(getSessionStorage(), DISTINCT_ID_STORAGE_KEY) ??
    readStorageValue(getLocalStorage(), DISTINCT_ID_STORAGE_KEY) ??
    readCookieValue(DISTINCT_ID_COOKIE_KEY);
  identity.anonymousId =
    identity.anonymousId ??
    readStorageValue(getSessionStorage(), ANONYMOUS_ID_STORAGE_KEY) ??
    readStorageValue(getLocalStorage(), ANONYMOUS_ID_STORAGE_KEY) ??
    readCookieValue(ANONYMOUS_ID_COOKIE_KEY);

  return identity;
}

/**
 * 将神策身份同步到本地缓存，供 Snowplow 首屏事件和 App H5 fallback 使用。
 */
export function syncSensorsIdentityToStorage(identity = getSensorsIdentity()): SensorsIdentity {
  writeStorageValue(getSessionStorage(), DISTINCT_ID_STORAGE_KEY, identity.distinctId);
  writeStorageValue(getSessionStorage(), ANONYMOUS_ID_STORAGE_KEY, identity.anonymousId);
  writeStorageValue(getLocalStorage(), DISTINCT_ID_STORAGE_KEY, identity.distinctId);
  writeStorageValue(getLocalStorage(), ANONYMOUS_ID_STORAGE_KEY, identity.anonymousId);
  writeCookieValue(DISTINCT_ID_COOKIE_KEY, identity.distinctId);
  writeCookieValue(ANONYMOUS_ID_COOKIE_KEY, identity.anonymousId);

  return identity;
}
